// <annotated-slideshow> — auto-advancing full-bleed slideshow (sections/annotated-slideshow.liquid).
// The active segment's CSS fill is the only clock: its animationend advances the slideshow, so the
// progress bar and the slide change cannot drift. Pausing freezes that fill (animation-play-state via
// [data-paused]) and resuming continues from the same point.
// Pause reasons are cumulative (user button, offscreen, tab hidden, theme editor block selection,
// finger on the track, keyboard focus inside): the fill only runs when none applies.
// Swipe is native scroll-snap; the active segment follows the scroll position live, except during a
// programmatic scroll started by goTo(), which already set it.
// Wrap-around: one inert clone of slide 1 sits after the last slide. Advancing from the last slide
// scrolls onto the clone, then jumps instantly back to the identical real slide 1 once settled.
// Videos follow the same state: only the active slide's visible video plays, only while nothing pauses
// the slideshow, never under reduced motion. Its loop is independent of the slide duration, which stays
// the clock. Each video sits over its still image and is only shown ([data-ready]) once it paints frames:
// a video without a frame (not started, rewound, frames dropped by iOS, autoplay refused) leaves the still
// visible instead of the black stage. Videos are rewound only off-screen, once the scroll has settled, so
// a visible video is never rewound. The clone carries no video: it shows slide 1's still.
if (!customElements.get('annotated-slideshow')) {
    const FILL_ANIMATION = 'annotated-slideshow-fill';
    const VISIBLE_THRESHOLD = 0.25;
    // Images start loading when the section is within one viewport height, ahead of the first swipe.
    const APPROACH_MARGIN = '100% 0px';
    // Settle detection for browsers without the scrollend event.
    const SCROLL_SETTLE_FALLBACK_MS = 150;
    const SUPPORTS_SCROLLEND = 'onscrollend' in window;
    class AnnotatedSlideshow extends HTMLElement {
        track = null;
        slides = [];
        segments = [];
        pauseButton = null;
        clone = null;
        activeIndex = 0;
        pauseReasons = new Set();
        controller = null;
        observer = null;
        approachObserver = null;
        settleTimer;
        scrollFrame = 0;
        // True while a goTo() scroll is in flight: live updates are muted until it settles.
        programmaticScroll = false;
        // Videos waiting for their first painted frame, so a repeated play() does not stack callbacks.
        pendingReady = new WeakSet();
        reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Same breakpoint as the section's media layers: crossing it changes which video is visible.
        mobileQuery = window.matchMedia('(max-width: 749px)');
        connectedCallback() {
            this.track = this.querySelector('.annotated-slideshow__track');
            if (!this.track)
                return;
            this.slides = Array.from(this.track.querySelectorAll('.annotated-slideshow__slide'));
            this.segments = Array.from(this.querySelectorAll('.annotated-slideshow__segment'));
            this.pauseButton = this.querySelector('.annotated-slideshow__pause');
            if (!this.slides.length)
                return;
            this.controller = new AbortController();
            const { signal } = this.controller;
            // A single slide has no controls and nothing to advance: only the pause reasons (and so its video)
            // apply.
            if (this.slides.length > 1) {
                this.appendClone();
                this.segments.forEach((segment, index) => segment.addEventListener('click', () => this.goTo(index), { signal }));
                this.pauseButton?.addEventListener('click', this.onPauseClick, { signal });
                this.querySelector('.annotated-slideshow__segments')?.addEventListener('animationend', this.onFillEnd, {
                    signal,
                });
                this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
                if (SUPPORTS_SCROLLEND)
                    this.track.addEventListener('scrollend', this.onScrollSettled, { signal });
                // A finger on the track holds the fill, so it cannot end mid-drag and fight the swipe.
                this.track.addEventListener('touchstart', this.onTouchStart, { passive: true, signal });
                this.track.addEventListener('touchend', this.onTouchEnd, { passive: true, signal });
                this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true, signal });
            }
            this.addEventListener('focusin', this.onFocusIn, { signal });
            this.addEventListener('focusout', this.onFocusOut, { signal });
            document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
            document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
            document.addEventListener('shopify:block:deselect', this.onBlockDeselect, { signal });
            this.mobileQuery.addEventListener('change', this.onBreakpointChange, { signal });
            // Slide 1 is active but its fill stays paused until the section is first seen.
            this.pauseReasons.add('offscreen');
            if (document.hidden)
                this.pauseReasons.add('hidden');
            this.applyPauseState();
            this.setActive(0);
            // Only slide 1's visible video is fetched ahead; the others load when their slide first plays.
            this.slideVideos(0)
                .filter((video) => this.isRendered(video))
                .forEach((video) => (video.preload = 'auto'));
            this.observer = new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1];
                // intersectionRatio, not isIntersecting: the latter stays true below the threshold.
                this.setPauseReason('offscreen', entry.intersectionRatio < VISIBLE_THRESHOLD);
            }, { threshold: VISIBLE_THRESHOLD });
            this.observer.observe(this);
            this.approachObserver = new IntersectionObserver((entries) => {
                if (!entries.some((entry) => entry.isIntersecting))
                    return;
                this.promoteLazyImages();
                this.approachObserver?.disconnect();
                this.approachObserver = null;
            }, { rootMargin: APPROACH_MARGIN });
            this.approachObserver.observe(this);
        }
        disconnectedCallback() {
            this.controller?.abort();
            this.controller = null;
            this.observer?.disconnect();
            this.observer = null;
            this.approachObserver?.disconnect();
            this.approachObserver = null;
            window.clearTimeout(this.settleTimer);
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = 0;
            this.programmaticScroll = false;
            this.clone?.remove();
            this.clone = null;
            this.pauseReasons.clear();
        }
        appendClone() {
            if (!this.track)
                return;
            const clone = this.slides[0].cloneNode(true);
            clone.classList.add('annotated-slideshow__slide--clone');
            // Not a real block: keep it out of the theme editor and the accessibility tree.
            clone.removeAttribute('data-shopify-editor-block');
            clone.removeAttribute('aria-roledescription');
            clone.removeAttribute('aria-label');
            clone.setAttribute('aria-hidden', 'true');
            clone.inert = true;
            // The still image underneath stands in for slide 1's video during the wrap-around: a second copy of
            // the video would only add a decoder at the moment iOS is most likely to drop frames.
            clone.querySelectorAll('video').forEach((video) => video.remove());
            this.track.append(clone);
            this.clone = clone;
        }
        goTo(index) {
            if (!this.track)
                return;
            const count = this.slides.length;
            const wraps = index >= count;
            const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
            // Without motion there is no visible transition to preserve: go straight to the real slide 1.
            const target = wraps && behavior === 'smooth' ? count : index % count;
            this.setActive(wraps ? 0 : index);
            // Mute live updates until this scroll settles, so a jump from slide 1 to 3 does not flash slide 2.
            // Only when the track actually moves: a no-op scrollTo fires no scrollend to clear the flag.
            this.programmaticScroll = Math.abs(this.track.scrollLeft - target * this.track.clientWidth) > 1;
            this.scrollToIndex(target, behavior);
        }
        scrollToIndex(index, behavior) {
            this.track?.scrollTo({ left: index * this.track.clientWidth, behavior });
        }
        setActive(index) {
            const previous = this.segments[this.activeIndex];
            previous?.classList.remove('is-active');
            previous?.removeAttribute('aria-current');
            this.activeIndex = index;
            this.syncVideos();
            const segment = this.segments[index];
            if (!segment)
                return;
            // Restart the fill from 0: the class is removed, a reflow is forced, then the class is re-added,
            // so the browser sees a new animation even when the same segment is re-activated.
            segment.classList.remove('is-active');
            void segment.offsetWidth;
            segment.classList.add('is-active');
            segment.setAttribute('aria-current', 'true');
        }
        setPauseReason(reason, active) {
            if (active) {
                this.pauseReasons.add(reason);
            }
            else {
                this.pauseReasons.delete(reason);
            }
            this.applyPauseState();
        }
        applyPauseState() {
            const paused = this.pauseReasons.size > 0;
            this.toggleAttribute('data-paused', paused);
            this.track?.setAttribute('aria-live', paused ? 'polite' : 'off');
            this.syncVideos();
            if (!this.pauseButton)
                return;
            const userPaused = this.pauseReasons.has('user');
            this.pauseButton.toggleAttribute('data-user-paused', userPaused);
            this.pauseButton.setAttribute('aria-label', (userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause) ?? '');
        }
        slideVideos(index) {
            return Array.from(this.slides[index]?.querySelectorAll('video') ?? []);
        }
        // Once the section approaches the viewport, the stills and images of every slide load, so a swipe
        // never reveals one that is still lazy and empty. Hidden breakpoint layers are skipped.
        promoteLazyImages() {
            this.track?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
                if (this.isRendered(image))
                    image.loading = 'eager';
            });
        }
        // False for the layer hidden at the current breakpoint (display: none).
        isRendered(element) {
            return element.getClientRects().length > 0;
        }
        syncVideos() {
            const playing = this.pauseReasons.size === 0 && !this.reducedMotion.matches;
            this.slides.forEach((_, index) => {
                this.slideVideos(index).forEach((video) => this.setVideoPlaying(video, playing && index === this.activeIndex && this.isRendered(video)));
            });
        }
        setVideoPlaying(video, shouldPlay) {
            if (shouldPlay && video.paused) {
                video.muted = true;
                this.revealOnFirstFrame(video);
                // A refused play (autoplay policy, Low Power Mode) keeps the video hidden over its still.
                video.play().catch(() => { });
            }
            else if (!shouldPlay && !video.paused) {
                video.pause();
            }
        }
        // [data-ready] shows the video over its still, and only once a frame is actually on screen.
        revealOnFirstFrame(video) {
            if (video.hasAttribute('data-ready') || this.pendingReady.has(video))
                return;
            this.pendingReady.add(video);
            const reveal = () => {
                this.pendingReady.delete(video);
                video.setAttribute('data-ready', '');
            };
            // requestVideoFrameCallback fires on a painted frame; timeupdate is the fallback for older browsers.
            if (typeof video.requestVideoFrameCallback === 'function') {
                video.requestVideoFrameCallback(reveal);
            }
            else {
                video.addEventListener('timeupdate', reveal, { once: true, signal: this.controller?.signal });
            }
        }
        // Off-screen videos go back to their first frame for their next visit. Only called once the scroll
        // has settled, so the rewound videos are out of view; they hide again until they paint a new frame.
        rewindInactiveVideos() {
            this.slides.forEach((_, index) => {
                if (index === this.activeIndex)
                    return;
                this.slideVideos(index).forEach((video) => {
                    if (video.currentTime === 0)
                        return;
                    video.currentTime = 0;
                    video.removeAttribute('data-ready');
                });
            });
        }
        onBreakpointChange = () => {
            this.syncVideos();
        };
        onFillEnd = (event) => {
            const { animationName, target } = event;
            if (animationName !== FILL_ANIMATION || target !== this.segments[this.activeIndex])
                return;
            this.goTo(this.activeIndex + 1);
        };
        // Live update, like SliderComponent.update(): the segment switches as soon as the swipe passes the
        // midpoint of a slide, instead of waiting for the scroll to settle. The clone maps to slide 1.
        syncActiveToScroll() {
            if (this.programmaticScroll || !this.track || !this.track.clientWidth)
                return;
            const index = Math.round(this.track.scrollLeft / this.track.clientWidth) % this.slides.length;
            if (index !== this.activeIndex)
                this.setActive(index);
        }
        onScroll = () => {
            if (!this.scrollFrame) {
                this.scrollFrame = requestAnimationFrame(() => {
                    this.scrollFrame = 0;
                    this.syncActiveToScroll();
                });
            }
            if (!SUPPORTS_SCROLLEND) {
                window.clearTimeout(this.settleTimer);
                this.settleTimer = window.setTimeout(this.onScrollSettled, SCROLL_SETTLE_FALLBACK_MS);
            }
        };
        onScrollSettled = () => {
            if (!this.track || !this.track.clientWidth)
                return;
            this.programmaticScroll = false;
            if (Math.round(this.track.scrollLeft / this.track.clientWidth) >= this.slides.length) {
                // Landed on the clone: jump without animation to the identical real slide 1.
                this.scrollToIndex(0, 'auto');
            }
            // Catch-up pass: a goTo() scroll interrupted by a swipe settles while live updates were muted.
            this.syncActiveToScroll();
            this.rewindInactiveVideos();
        };
        onPauseClick = () => {
            this.setPauseReason('user', !this.pauseReasons.has('user'));
        };
        onTouchStart = () => {
            // The finger takes over any goTo() scroll in flight: live segment updates resume right away.
            this.programmaticScroll = false;
            this.setPauseReason('touch', true);
        };
        onTouchEnd = () => {
            this.setPauseReason('touch', false);
        };
        // Keyboard focus only: a mouse click on a segment must not hold the fill.
        onFocusIn = (event) => {
            if (event.target.matches(':focus-visible'))
                this.setPauseReason('focus', true);
        };
        onFocusOut = (event) => {
            if (this.contains(event.relatedTarget))
                return;
            this.setPauseReason('focus', false);
        };
        onVisibilityChange = () => {
            this.setPauseReason('hidden', document.hidden);
        };
        onBlockSelect = (event) => {
            const index = this.slides.indexOf(event.target);
            if (index === -1)
                return;
            this.setPauseReason('editor', true);
            this.goTo(index);
        };
        onBlockDeselect = (event) => {
            if (!this.slides.includes(event.target))
                return;
            this.setPauseReason('editor', false);
        };
    }
    customElements.define('annotated-slideshow', AnnotatedSlideshow);
}
